import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Building2,
  Maximize,
  CalendarCheck,
  Heart,
  Share2,
  ArrowRight,
} from "lucide-react";

const projects = [
  {
    title: "Poulomi Florique",
    location: "Thanisandra, Bangalore",
    price: "₹1.45 Cr",
    status: "Under Construction",
    configs: "3, 4 BHK",
    area: "1,585 – 2,740 ft²",
    possession: "Dec 2028",
    image:
      "https://images.unsplash.com/photo-1512917774080-9991f1c4c750",
    url: "/poulomi-florique",
  },
  {
    title: "Sobha OneWorld",
    location: "Greater Whitefield, Bengaluru",
    price: "₹13,999/sft",
    status: "New Launch",
    configs: "3, 4 BHK",
    area: "1,100 – 1,542 ft²",
    possession: "Ready",
    image:
      "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d",
    url: "/sobha-one-world",
  },
  {
    title: "Prestige Waterford",
    location: "Bangalore, Karnataka",
    price: "₹2.1 Cr",
    status: "New Launch",
    configs: "3, 4 BHK",
    area: "1,800 – 2,950 ft²",
    possession: "Mar 2029",
    image:
      "https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde",
    url: "/rivera-edition",
  },
];

const FeaturedProjects = () => {

  // ================= SHARE FUNCTION =================
  const handleShare = (project, e) => {
    e.preventDefault();

    const message = `🏗 *${project.title}*

📍 Location: ${project.location}

💰 Starting Price: ${project.price}
🏢 Status: ${project.status}
🛏 Configurations: ${project.configs}
📐 Area: ${project.area}
📅 Possession: ${project.possession}

🔗 Project Link:
${window.location.origin}${project.url}

Interested in this project.`;

    const whatsappURL = `https://wa.me/918088687429?text=${encodeURIComponent(
      message
    )}`;

    window.open(whatsappURL, "_blank");
  };

  return (
    <section className="w-full bg-gradient-to-b from-[#F8FAFC] to-[#EEF2F7] py-20 overflow-hidden">

      <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-14">

        {/* ================= HEADER ================= */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">

          {/* LEFT */}
          <div className="max-w-2xl">

            <span className="inline-flex items-center gap-2 bg-red-50 text-red-600 border border-red-100 px-4 py-2 rounded-full text-sm font-semibold mb-5">
              Featured Developments
            </span>

            <h2 className="text-4xl md:text-5xl font-black tracking-tight text-gray-900 leading-tight">
              New & Ongoing
              <span className="text-red-600"> Projects</span>
            </h2>

            <p className="text-gray-500 text-lg mt-5 leading-relaxed">
              Handpicked residential developments from trusted builders —
              compare configurations, pricing and possession at a glance.
            </p>
          </div>

          {/* RIGHT BUTTON */}
          <button className="group bg-red-600 hover:bg-red-700 text-white px-7 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg hover:shadow-red-500/30 hover:-translate-y-1 flex items-center gap-3 w-fit">

            View All Projects

            <ArrowRight
              size={18}
              className="group-hover:translate-x-1 transition"
            />
          </button>
        </div>

        {/* ================= PROJECT GRID ================= */}
        <div className="flex lg:grid lg:grid-cols-3 gap-8 overflow-x-auto lg:overflow-visible snap-x snap-mandatory pb-4 no-scrollbar">

          {projects.map((item, index) => (
            <Link
              key={index}
              to={item.url}
              className="group min-w-[88%] sm:min-w-[60%] lg:min-w-0 bg-white rounded-[32px] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100 snap-start"
            >

              {/* ================= IMAGE ================= */}
              <div className="relative overflow-hidden">

                <img
                  src={item.image}
                  alt={item.title}
                  className="h-[300px] w-full object-cover group-hover:scale-110 transition duration-700"
                />

                {/* OVERLAY */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>

                {/* PROJECT STATUS */}
                <div className="absolute top-5 left-5">
                  <span className="bg-white/90 backdrop-blur-xl text-gray-900 text-xs px-4 py-2 rounded-full font-semibold shadow-md">
                    {item.status}
                  </span>
                </div>

                {/* ACTION BUTTONS */}
                <div className="absolute top-5 right-5 flex items-center gap-3">

                  {/* FAVORITE */}
                  <button
                    onClick={(e) => e.preventDefault()}
                    className="w-11 h-11 rounded-full bg-white/90 backdrop-blur-xl flex items-center justify-center hover:bg-red-50 hover:text-red-600 transition shadow-md"
                  >
                    <Heart size={18} />
                  </button>

                  {/* SHARE BUTTON */}
                  <button
                    onClick={(e) => handleShare(item, e)}
                    className="w-11 h-11 rounded-full bg-[#25D366] flex items-center justify-center hover:scale-110 transition shadow-lg"
                  >
                    <Share2 size={18} className="text-white" />
                  </button>
                </div>

                {/* PRICE CARD */}
                <div className="absolute bottom-5 left-5">
                  <div className="bg-white/95 backdrop-blur-xl rounded-2xl px-5 py-3 shadow-xl">

                    <div className="text-2xl font-black text-red-600">
                      {item.price}
                    </div>

                    <div className="text-xs text-gray-500 font-medium">
                      Starting Price
                    </div>
                  </div>
                </div>
              </div>

              {/* ================= CONTENT ================= */}
              <div className="p-6">

                {/* TITLE */}
                <h3 className="text-2xl font-bold text-gray-900 group-hover:text-red-600 transition">
                  {item.title}
                </h3>

                {/* LOCATION */}
                <div className="flex items-center gap-2 text-gray-500 text-sm mt-3">
                  <MapPin size={16} className="text-red-500" />

                  {item.location}
                </div>

                {/* FEATURES */}
                <div className="grid grid-cols-3 gap-3 mt-7">

                  {/* CONFIGS */}
                  <div className="bg-gray-50 rounded-2xl py-4 flex flex-col items-center justify-center border border-gray-100">
                    <Building2 size={18} className="text-red-600 mb-2" />

                    <span className="text-sm font-bold text-gray-900">
                      {item.configs}
                    </span>

                    <span className="text-[11px] text-gray-500">
                      Configuration
                    </span>
                  </div>

                  {/* AREA */}
                  <div className="bg-gray-50 rounded-2xl py-4 flex flex-col items-center justify-center border border-gray-100">
                    <Maximize size={18} className="text-red-600 mb-2" />

                    <span className="text-sm font-bold text-gray-900">
                      {item.area}
                    </span>

                    <span className="text-[11px] text-gray-500">
                      Area Range
                    </span>
                  </div>

                  {/* POSSESSION */}
                  <div className="bg-gray-50 rounded-2xl py-4 flex flex-col items-center justify-center border border-gray-100">
                    <CalendarCheck size={18} className="text-red-600 mb-2" />

                    <span className="text-sm font-bold text-gray-900">
                      {item.possession}
                    </span>

                    <span className="text-[11px] text-gray-500">
                      Possession
                    </span>
                  </div>
                </div>

                {/* CTA */}
                <div className="mt-7 flex gap-2">
                  <span className="flex-1 bg-gray-900 group-hover:bg-red-600 text-white py-4 rounded-2xl font-semibold transition-all duration-300 flex items-center justify-center gap-3">
                    View Project Details
                    <ArrowRight
                      size={18}
                      className="group-hover/btn:translate-x-1 transition"
                    />
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(
                        "https://wa.me/message/MDG2ZSZNG6P6G1",
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                    className="px-5 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-semibold text-sm transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-red-500/20"
                    aria-label={`Contact about ${item.title}`}
                  >
                    Contact
                  </button>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProjects;